import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Search,
  Filter,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Calendar,
  MapPin,
  DollarSign,
  Clock,
  Eye,
  Download,
  CheckCircle,
  RotateCcw,
  AlertCircle,
  XCircle,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobsList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalJobs, setTotalJobs] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Filters
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'all',
    ownership: searchParams.get('ownership') || 'all',
    search: searchParams.get('search') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    sortBy: searchParams.get('sortBy') || 'created_at',
    sortOrder: searchParams.get('sortOrder') || 'desc'
  });
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pageSize] = useState(25);
  
  useEffect(() => {
    fetchJobs();
  }, [filters, currentPage]);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        params.set(key, value);
      }
    });
    if (currentPage > 1) {
      params.set('page', currentPage.toString());
    }
    setSearchParams(params, { replace: true });
  }, [filters, currentPage, setSearchParams]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs`);
      let filteredJobs = response.data;

      // Apply filters
      if (filters.status !== 'all') {
        filteredJobs = filteredJobs.filter(job => job.status === filters.status);
      }

      if (filters.ownership === 'originated') {
        filteredJobs = filteredJobs.filter(job => job.originator_id === user.id);
      } else if (filters.ownership === 'received') {
        filteredJobs = filteredJobs.filter(job => job.accepted_by === user.id);
      } else if (filters.ownership === 'passed_back') {
        // Show jobs that were passed back and user can potentially receive
        // This is a simplification - ideally we'd track who specifically passed back each job
        filteredJobs = filteredJobs.filter(job => 
          job.status === 'passed_back' && 
          job.originator_id !== user.id &&
          user.can_receive_jobs
        );
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredJobs = filteredJobs.filter(job => 
          job.title.toLowerCase().includes(searchLower) ||
          job.location.toLowerCase().includes(searchLower) ||
          (job.job_specification && job.job_specification.toLowerCase().includes(searchLower)) ||
          (job.tyre_specification && job.tyre_specification.toLowerCase().includes(searchLower)) ||
          job.id.toLowerCase().includes(searchLower)
        );
      }

      if (filters.dateFrom) {
        filteredJobs = filteredJobs.filter(job => 
          new Date(job.created_at) >= new Date(filters.dateFrom)
        );
      }

      if (filters.dateTo) {
        filteredJobs = filteredJobs.filter(job => 
          new Date(job.created_at) <= new Date(filters.dateTo + 'T23:59:59')
        );
      }

      // Sort
      filteredJobs.sort((a, b) => {
        const aVal = a[filters.sortBy];
        const bVal = b[filters.sortBy];
        const modifier = filters.sortOrder === 'desc' ? -1 : 1;
        
        if (filters.sortBy === 'quoted_price') {
          return (parseFloat(aVal) - parseFloat(bVal)) * modifier;
        }
        
        return aVal < bVal ? -1 * modifier : aVal > bVal ? 1 * modifier : 0;
      });

      setTotalJobs(filteredJobs.length);
      
      // Pagination
      const startIndex = (currentPage - 1) * pageSize;
      const paginatedJobs = filteredJobs.slice(startIndex, startIndex + pageSize);
      
      setJobs(paginatedJobs);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSort = (sortBy) => {
    const newOrder = filters.sortBy === sortBy && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    setFilters(prev => ({ ...prev, sortBy, sortOrder: newOrder }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleExpanded = (jobId, e) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(jobId)) {
      newExpanded.delete(jobId);
    } else {
      newExpanded.add(jobId);
    }
    setExpandedRows(newExpanded);
  };

  const userPassedBackJob = (job) => {
    // Check if current user passed back this job by looking for timeline entries
    // For now, we'll use a simpler check - if job is passed_back and user can receive it
    return job.status === 'passed_back' && user.can_receive_jobs;
  };

  const getStatusBadge = (status) => {
    const badges = {
      posted: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Live' },
      accepted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Assigned' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      passed_back: { bg: 'bg-red-100', text: 'text-red-800', label: 'Reassigned' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' }
    };
    return badges[status] || badges.posted;
  };

  const getUrgencyColor = (urgency) => {
    const colors = {
      low: 'text-green-600',
      medium: 'text-yellow-600',
      high: 'text-orange-600',
      emergency: 'text-red-600'
    };
    return colors[urgency] || colors.medium;
  };

  const formatJobCode = (id) => {
    return `JOP-${id.slice(0, 8).toUpperCase()}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const exportCSV = () => {
    // Simple CSV export
    const headers = ['Job Code', 'Posted At', 'Location', 'Job Spec', 'Quoted Price', 'Status', 'Originator', 'Receiver'];
    const csvData = jobs.map(job => [
      formatJobCode(job.id),
      formatDate(job.created_at),
      job.location,
      job.job_specification || job.tyre_specification || 'No specification',
      `$${job.quoted_price}`,
      job.status,
      job.originator_business,
      job.accepted_by_business || 'Unassigned'
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jop-jobs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleAcceptJob = async (jobId, e) => {
    e.stopPropagation(); // Prevent row click
    try {
      await axios.post(`${API}/jobs/accept`, { job_id: jobId });
      toast.success('Job accepted successfully!');
      fetchJobs(); // Refresh the list
    } catch (error) {
      console.error('Failed to accept job:', error);
      const message = error.response?.data?.detail || 'Failed to accept job';
      toast.error(message);
    }
  };

  const handleCompleteJob = async (jobId, e) => {
    e.stopPropagation(); // Prevent row click
    try {
      await axios.post(`${API}/jobs/complete`, { job_id: jobId });
      toast.success('Job completed! Commission calculated.');
      fetchJobs(); // Refresh the list
    } catch (error) {
      console.error('Failed to complete job:', error);
      toast.error('Failed to complete job');
    }
  };

  const totalPages = Math.ceil(totalJobs / pageSize);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">Jobs List</h1>
              <span className="ml-3 text-sm text-gray-500">({totalJobs} jobs)</span>
            </div>
            
            <button
              onClick={exportCSV}
              className="jop-button-secondary flex items-center"
            >
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="jop-card p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="jop-select"
              >
                <option value="all">All Statuses</option>
                <option value="posted">Live</option>
                <option value="accepted">Assigned</option>
                <option value="completed">Completed</option>
                <option value="passed_back">Reassigned</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Ownership</label>
              <select
                value={filters.ownership}
                onChange={(e) => handleFilterChange('ownership', e.target.value)}
                className="jop-select"
              >
                <option value="all">All Jobs</option>
                <option value="originated">I Posted</option>
                <option value="received">I Accepted</option>
                {user.can_receive_jobs && (
                  <option value="passed_back">My Passed Back</option>
                )}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                className="jop-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                className="jop-input"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="jop-input pl-10"
                placeholder="Search by job code, location, or specification..."
              />
            </div>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="jop-card overflow-hidden">
          {jobs.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs match your filters</h3>
              <p className="text-gray-600">Try adjusting your search criteria or filters.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('id')}
                      >
                        Job Code
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('created_at')}
                      >
                        Posted At {filters.sortBy === 'created_at' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Spec
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('quoted_price')}
                      >
                        Price {filters.sortBy === 'quoted_price' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th 
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort('status')}
                      >
                        Status {filters.sortBy === 'status' && (filters.sortOrder === 'desc' ? '↓' : '↑')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Originator
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Receiver
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs.map((job) => (
                      <React.Fragment key={job.id}>
                        <tr 
                          className="hover:bg-gray-50 cursor-pointer"
                          onClick={() => navigate(`/jobs/${job.id}`)}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {job.status === 'passed_back' && (
                                <button
                                  onClick={(e) => toggleExpanded(job.id, e)}
                                  className="mr-2 text-gray-400 hover:text-gray-600"
                                  title="Toggle pass-back details"
                                >
                                  {expandedRows.has(job.id) ? 
                                    <ChevronUp className="h-4 w-4" /> : 
                                    <ChevronDown className="h-4 w-4" />
                                  }
                                </button>
                              )}
                              <div className="text-sm font-medium text-gray-900">
                                {formatJobCode(job.id)}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(job.created_at)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {new Date(job.created_at).toLocaleTimeString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm text-gray-900">
                              <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                              {job.location}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900 max-w-xs truncate">
                              {job.job_specification || job.tyre_specification || 'No specification'}
                            </div>
                            <div className={`text-xs font-medium ${getUrgencyColor(job.urgency_level)}`}>
                              {job.urgency_level.toUpperCase()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center text-sm font-medium text-gray-900">
                              <DollarSign className="h-4 w-4 mr-1 text-gray-400" />
                              {job.quoted_price}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(job.status).bg} ${getStatusBadge(job.status).text}`}>
                                {getStatusBadge(job.status).label}
                              </span>
                              {job.status === 'passed_back' && (
                                <AlertCircle className="h-4 w-4 ml-2 text-orange-500" title="Passed back - see details" />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{job.originator_business}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {job.accepted_by_business || 'Unassigned'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              {/* Funding Pill - for unfunded jobs (originators and admins only) */}
                              {job.status === 'posted' && 
                               (job.payment_status === 'unfunded' || !job.payment_status) && 
                               (user.id === job.originator_id || user.is_admin) && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/jobs/${job.id}?open=fund`);
                                  }}
                                  className="text-blue-600 hover:text-blue-800 text-xs font-medium bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 flex items-center"
                                  title="Fund job with escrow"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Unfunded – Fund now
                                </button>
                              )}
                              
                              {/* Accept Job - only for funded jobs */}
                              {user.can_receive_jobs && 
                               job.status === 'posted' && 
                               job.payment_status === 'funded' &&
                               job.originator_id !== user.id && (
                                <button
                                  onClick={(e) => handleAcceptJob(job.id, e)}
                                  className="text-green-600 hover:text-green-800 p-1 rounded hover:bg-green-50"
                                  title="Accept Job"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              )}
                              
                              {/* Unfunded Job - Disabled Accept with Tooltip */}
                              {user.can_receive_jobs && 
                               job.status === 'posted' && 
                               (job.payment_status === 'unfunded' || !job.payment_status) &&
                               job.originator_id !== user.id && (
                                <div className="relative group">
                                  <button
                                    disabled={true}
                                    className="text-gray-400 p-1 rounded cursor-not-allowed"
                                    title="Job must be funded before acceptance"
                                  >
                                    <AlertCircle className="h-4 w-4" />
                                  </button>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-nowrap">
                                    Originator must fund before acceptance
                                  </div>
                                </div>
                              )}
                              
                              {/* Passed back jobs - show warning for no re-acceptance */}
                              {job.status === 'passed_back' && user.can_receive_jobs && job.originator_id !== user.id && (
                                <div className="flex items-center text-xs text-orange-600">
                                  <XCircle className="h-4 w-4 mr-1" />
                                  Admin needed
                                </div>
                              )}
                              
                              {/* Complete Job - for jobs user has accepted */}
                              {job.accepted_by === user.id && job.status === 'accepted' && (
                                <button
                                  onClick={(e) => handleCompleteJob(job.id, e)}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                                  title="Mark Complete"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                              )}
                              
                              {/* Pass Back - for jobs user has accepted */}
                              {job.accepted_by === user.id && job.status === 'accepted' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/jobs/${job.id}`);
                                  }}
                                  className="text-orange-600 hover:text-orange-800 p-1 rounded hover:bg-orange-50"
                                  title="Pass Back (opens detail)"
                                >
                                  <RotateCcw className="h-4 w-4" />
                                </button>
                              )}
                              
                              {/* View Details - always available */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/jobs/${job.id}`);
                                }}
                                className="text-gray-600 hover:text-gray-800 p-1 rounded hover:bg-gray-50"
                                title="View Details"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        
                        {/* Expandable row for passed back job details */}
                        {job.status === 'passed_back' && expandedRows.has(job.id) && (
                          <tr>
                            <td colSpan="9" className="px-6 py-4 bg-red-50">
                              <div className="border border-red-200 rounded-lg p-4">
                                <div className="flex items-start">
                                  <AlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                                  <div className="flex-1">
                                    <h4 className="text-sm font-medium text-red-900 mb-2">
                                      Job Passed Back - Admin Intervention Required
                                    </h4>
                                    {job.pass_back_reason && (
                                      <div className="mb-3">
                                        <p className="text-xs font-medium text-red-800 mb-1">Reason:</p>
                                        <p className="text-sm text-red-700 bg-red-100 p-2 rounded">
                                          {job.pass_back_reason}
                                        </p>
                                      </div>
                                    )}
                                    <div className="text-xs text-red-600">
                                      <p>• This job was passed back and requires administrative approval before it can be reassigned</p>
                                      <p>• Contact an administrator to review and reopen this job</p>
                                      <p>• The original receiver cannot re-accept this job without admin intervention</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalJobs)} of {totalJobs} jobs
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const page = i + 1;
                      return (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          className={`px-3 py-1 rounded-lg border ${
                            currentPage === page 
                              ? 'bg-amber-500 text-white border-amber-500' 
                              : 'border-gray-300 hover:bg-gray-100'
                          }`}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JobsList;